import React from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  width?: number;
  height?: number;
}

/**
 * High-performance image component that automatically serves:
 * - AVIF format for modern browsers (best compression)
 * - WebP format for broader compatibility
 * - JPEG fallback for older browsers
 * - Responsive sizes for different screen sizes
 */
export default function OptimizedImage({ 
  src, 
  alt, 
  className = '', 
  sizes = '(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1200px, 1920px',
  priority = false,
  width,
  height
}: OptimizedImageProps) {
  // Extract filename without extension
  const name = src.replace(/\.(jpg|jpeg|png|webp|avif)$/i, '');
  const basePath = src.startsWith('/') ? `/optimized${name}` : `/optimized/${name}`;
  
  return (
    <picture>
      {/* Ultra-modern AVIF format - best compression, maintain quality */}
      <source
        media="(max-width: 480px)"
        srcSet={`${basePath}-sm.avif`}
        type="image/avif"
        sizes="480px"
      />
      <source
        media="(max-width: 768px)"
        srcSet={`${basePath}-md.avif`}
        type="image/avif"
        sizes="768px"
      />
      <source
        media="(max-width: 1200px)"
        srcSet={`${basePath}-lg.avif`}
        type="image/avif"
        sizes="1200px"
      />
      <source
        srcSet={`${basePath}-xl.avif`}
        type="image/avif"
        sizes="1920px"
      />

      {/* WebP format - excellent compression with broad support */}
      <source
        media="(max-width: 480px)"
        srcSet={`${basePath}-sm.webp`}
        type="image/webp"
        sizes="480px"
      />
      <source
        media="(max-width: 768px)"
        srcSet={`${basePath}-md.webp`}
        type="image/webp"
        sizes="768px"
      />
      <source
        media="(max-width: 1200px)"
        srcSet={`${basePath}-lg.webp`}
        type="image/webp"
        sizes="1200px"
      />
      <source
        srcSet={`${basePath}-xl.webp`}
        type="image/webp"
        sizes="1920px"
      />
      
      {/* Fallback JPEG for maximum compatibility */}
      <img
        src={`${basePath}.jpg`}
        alt={alt}
        className={className}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        width={width}
        height={height}
        sizes={sizes}
        style={{
          // Prevent layout shift
          aspectRatio: width && height ? `${width} / ${height}` : undefined
        }}
      />
    </picture>
  );
} 